import { useLocation, useNavigate } from "react-router-dom";
import TeamInfoMembers from "./TeamInfoMembers";
import TeamInfoPhotos from "./TeamInfoPhotos";
import { Images } from "../../../../utils/imageConstants";
import { setActiveTab } from "../../../../redux/slice/activeTabSlice";
import { useCallback, useEffect, useState } from "react";
import TeamInfoStats from "./TeamInfoStats";
import type { ITeamDetails, TabTeamsKey, IPlayers } from "../types/teams";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { Api } from "../../../../services/interceptor";
import TeamMatches from "./TeamMatches";
import GalleryDetails from "../../tournaments/tournamentsDetails/gallery/GalleryDetails";
import { formatDate } from "../../../../helper/helper";
import { buildTeamInviteShareText, buildTeamPlayerInviteUrl } from "../../../../utils/teamInviteLink";
import toast from "react-hot-toast";
import {
  exportMembersToPDF,
  exportStatsToPDF,
  exportMatchesToPDF,
  // exportAllTabsToPDF,
} from "../../../../utils/pdfExport";
import type { IMatchSummary } from "../../tournaments/tournamentsDetails/matches/types/IMatches";
import UpdateTeamModal from "./UpdateTeamModal";
import SearchPlayerModal from "../../../myTeam/components/SearchPlayerModal";
import { assignCoAdmin } from "../../../myTeam/api/myTeamApi";
import type { IPlayerSearchResult } from "../../../myTeam/types/myTeam";

const TeamInfo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [activeTeamsTab, setActiveTeamsTab] = useState<TabTeamsKey>("members");
  const teamId = sessionStorage.getItem("teamId");
  const teamSnapshotId = sessionStorage.getItem("teamSnapshotId");
  const sessionTeamCode = sessionStorage.getItem("teamCode");
  const sessionLeagueId = sessionStorage.getItem("leagueId");
  const tournamentId = sessionStorage.getItem("tournamentId");
  const isGalleryDetails = location.pathname === "/tournaments/team-info/gallery-details";
  const galleryItem = (
    location.state as {
      galleryItem?: { match: string; date: string; photoCount: number };
    }
  )?.galleryItem;

  const [updateTeamModal, setUpdateTeamModal] = useState<{
    open: boolean;
    teamId: string;
    teamName: string;
    teamSnapshotId: string;
  }>({ open: false, teamId: "", teamName: "", teamSnapshotId: "" });

  const [addPlayersModalOpen, setAddPlayersModalOpen] = useState(false);

  const TABS: Array<{ key: TabTeamsKey; label: string }> = [
    { key: "members", label: "Members" },
    { key: "matches", label: "Matches" },
    { key: "stats", label: "Stats" },
    { key: "photos", label: "Photos" },
  ];

  const { data: teamDetails = [] } = useQuery<ITeamDetails[]>({
    queryKey: [QUERY_KEY.teamsSnapShotByTeamId, teamId],
    queryFn: async () => {
      const url = `${QUERY_KEY.teamsSnapShotByTeamId}/${teamId}`;
      const response = await Api.get(url);
      return response?.data || [];
    },
  });

  // Fetch members data for export
  const { data: allMembers = [] } = useQuery<IPlayers[]>({
    queryKey: [QUERY_KEY.getAllPlayers, teamSnapshotId],
    queryFn: async () => {
      if (!teamSnapshotId) return [];
      const url = `${QUERY_KEY.getAllPlayers}/${teamSnapshotId}`;
      const response = await Api.get(url);
      return response?.data || [];
    },
    enabled: !!teamSnapshotId,
  });

  // Fetch stats data for export
  const { data: allPlayersStats = [] } = useQuery<any[]>({
    queryKey: [QUERY_KEY.getTeamPlayerStats, teamSnapshotId, tournamentId],
    queryFn: async () => {
      if (!teamSnapshotId || !tournamentId) return [];
      const url = `${QUERY_KEY.getTeamPlayerStats}?teamSnapshotId=${teamSnapshotId}&tournamentId=${tournamentId}`;
      const response = await Api.get(url);
      return response?.data || [];
    },
    enabled: !!teamSnapshotId && !!tournamentId,
  });

  // Fetch matches data for export
  const { data: allMatches = [] } = useQuery<IMatchSummary[]>({
    queryKey: [QUERY_KEY.matchByTeamAndStatus, teamSnapshotId],
    queryFn: async () => {
      if (!teamSnapshotId) return [];
      const params: Record<string, any> = {
        teamId: teamSnapshotId,
        status: "ENDED",
        page: 0,
        size: 100,
      };
      const response = await Api.get(QUERY_KEY.matchByTeamAndStatus, { params });
      return response?.data?.matchResponseList ?? [];
    },
    enabled: !!teamSnapshotId,
  });

  // Export handlers
  // const handleExportAllData = async () => {
  //   const teamName = teamDetails[0]?.teamName || "Team";
  //   await exportAllTabsToPDF(allMembers, allPlayersStats, allMatches, teamName);
  // };

  const handleExportCurrentTab = async () => {
    const teamName = teamDetails[0]?.teamName || "Team";
    if (activeTeamsTab === "members") {
      await exportMembersToPDF(allMembers, teamName);
    } else if (activeTeamsTab === "stats") {
      await exportStatsToPDF(allPlayersStats, teamName);
    } else if (activeTeamsTab === "matches") {
      await exportMatchesToPDF(allMatches, teamName, "past");
    }
  };

  const currentTeamSnapshot =
    teamDetails.find((t) => t.teamSnapshotId === teamSnapshotId) ?? teamDetails[0];

  const handleAddPlayersFromSearch = useCallback(
    async (players: IPlayerSearchResult[]) => {
      const tid = teamId?.trim();
      if (!tid) {
        toast.error("Team not found.");
        return;
      }
      if (players.length === 0) return;
      try {
        await assignCoAdmin(tid, players.map((p) => p.playerId));
        toast.success("Players added successfully.");
        setAddPlayersModalOpen(false);
        const snap = teamSnapshotId?.trim();
        if (snap) {
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.getAllPlayers, snap],
          });
        }
      } catch (err) {
        console.error("Failed to add players", err);
        toast.error("Failed to add players. Please try again.");
      }
    },
    [teamId, teamSnapshotId, queryClient]
  );

  const openEditTeam = () => {
    const tid = teamId?.trim() || currentTeamSnapshot?.teamId?.trim();
    const snap =
      teamSnapshotId?.trim() || currentTeamSnapshot?.teamSnapshotId?.trim();
    const name = currentTeamSnapshot?.teamName?.trim() || teamDetails[0]?.teamName || "";
    if (!tid || !snap) {
      toast.error("Team details are still loading. Try again in a moment.");
      return;
    }
    setUpdateTeamModal({
      open: true,
      teamId: tid,
      teamName: name,
      teamSnapshotId: snap,
    });
  };

  const handleShareTeam = async () => {
    const inviteTeamId = teamId?.trim() || currentTeamSnapshot?.teamId;
    const inviteSnapshotId = teamSnapshotId?.trim() || currentTeamSnapshot?.teamSnapshotId;
    if (!inviteTeamId || !inviteSnapshotId) {
      toast.error("Team details are still loading. Try again in a moment.");
      return;
    }
    const inviteLeagueId =
      currentTeamSnapshot?.leagueId?.trim() ||
      sessionLeagueId?.trim() ||
      undefined;

    const inviteUrl = buildTeamPlayerInviteUrl({
      teamCode:
        currentTeamSnapshot?.teamCode?.trim() ||
        sessionTeamCode?.trim() ||
        undefined,
      teamSnapshotName: currentTeamSnapshot?.teamSnapshotName,
      teamName: currentTeamSnapshot?.teamName,
      leagueId: inviteLeagueId,
      teamId: inviteTeamId,
      teamSnapshotId: inviteSnapshotId,
    });
    const shareText = buildTeamInviteShareText(inviteUrl);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Join ${currentTeamSnapshot?.teamName ?? "team"}`,
          text: shareText,
          url: inviteUrl,
        });
        return;
      }
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Invite message copied to clipboard");
    } catch {
      toast.error("Unable to share or copy. Copy the link from your browser if this persists.");
    }
  };

  useEffect(() => {
    const st = (location.state ?? {}) as { lastTeamsTab?: TabTeamsKey };
    if (st.lastTeamsTab) {
      setActiveTeamsTab(st.lastTeamsTab);
      const { lastTeamsTab, ...rest } = st;
      navigate(location.pathname + location.search, { state: rest, replace: true });
    }
  }, []);

  useEffect(() => {
    if (isGalleryDetails) {
      setActiveTeamsTab("photos");
    }
  }, [isGalleryDetails]);

  useEffect(() => {
    document.body.classList.add("route-tournaments-team-info");
    return () => document.body.classList.remove("route-tournaments-team-info");
  }, []);

  const handleTeamsTabClick = (key: TabTeamsKey) => {
    if (isGalleryDetails) {
      navigate("/tournaments/team-info", { replace: true });
    }
    setActiveTeamsTab(key);
  };

  return (
    <div>
      <div className="team-info-wrapper">
        <div className="background-image-wrapper">
          <div className="team-info">
            <div className="left-block">
              <div className="name-logo">
                <img src={Images.team_logo} alt="team-logo" />
                <p>{teamDetails[0]?.teamName}</p>
              </div>
              {/* <div className="players">
              <button>c MS Dhoni</button>
              <button>vc Virat Kohli</button>
            </div> */}
            </div>
            <div className="join-button">
              <div className="myteam-detail-header-right team-info-hero-actions">
                {/* <button
                  type="button"
                  className="myteam-detail-share"
                  onClick={() => void handleShareTeam()}
                  aria-label="Share team invite link"
                >
                  <img src={Images.share_icon} alt="" />
                  Share
                </button>
                <button
                  type="button"
                  className="myteam-detail-edit"
                  onClick={openEditTeam}
                  aria-label="Edit team"
                >
                  Edit
                </button> */}
              </div>
            </div>
          </div>
        </div>
        <div className="team-info-wrap">
          <div className="ct-header">
            <img
              src={Images.previous_icon}
              alt="Go back"
              onClick={() => { navigate("/leagues-home"); dispatch(setActiveTab("teams")); }}
              className="ti-back"
            />
            <h1 className="heading">Team Info</h1>

            <div className="ti-actions">
              <div className="ti-select">
                {/* <select aria-label="Tournament Name" defaultValue="">
                  <option value="" disabled>
                    {teamDetails[0]?.tournament?.tournamentName}
                  </option>
                </select> */}
                <span className="ti-caret">▾</span>
              </div>
            </div>
          </div>
          <div className="tab-section">
            <div className="ti-tabs" role="tablist" aria-label="Team sections">
              {TABS.map((t) => {
                const isActive = activeTeamsTab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    className={`ti-tab ${isActive ? "is-active" : ""}`}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`panel-${t.key}`}
                    id={`tab-${t.key}`}
                    tabIndex={0}
                    onClick={() => handleTeamsTabClick(t.key)}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {(activeTeamsTab === "members" ||
              activeTeamsTab === "matches" ||
              activeTeamsTab === "stats") &&
              !isGalleryDetails && (
                <div className="buttons">
                  {activeTeamsTab === "members" && (
                    <>
                      {/* <button
                        type="button"
                        className="add-player-button"
                        onClick={() => setAddPlayersModalOpen(true)}
                        aria-label="Add players to team"
                      >
                        ADD PLAYERS
                      </button> */}
                    </>
                  )}
                  <button
                    type="button"
                    className="export-pdf-button"
                    onClick={handleExportCurrentTab}
                  >
                    Export PDF
                  </button>
                </div>
              )}
          </div>
          {isGalleryDetails && galleryItem && (
            <div className="team-info-gallery-details-header">
              <div className="team-info-gallery-details-header__inner">
                <img
                  src={Images.white_back_arrow}
                  alt="Back"
                  className="team-info-gallery-details-back"
                  onClick={() => navigate(-1)}
                />
                <div className="team-info-gallery-details-titles">
                  <p className="team-info-gallery-details-title">{galleryItem.match}</p>
                  <p className="team-info-gallery-details-meta">
                    {formatDate(galleryItem.date)} | {galleryItem.photoCount}{" "}
                    {galleryItem.photoCount === 1 ? "Photo" : "Photos"}
                  </p>
                </div>
              </div>
            </div>
          )}
          {isGalleryDetails ? (
            <div className="tournament-details tab-content team-info-gallery-details-panel">
              <GalleryDetails />
            </div>
          ) : (
            <>
              {activeTeamsTab === "members" && (
                <TeamInfoMembers
                  captainPlayerId={currentTeamSnapshot?.captainPlayerId ?? undefined}
                  viceCaptainPlayerId={currentTeamSnapshot?.viceCaptainPlayerId ?? undefined}
                  wkPlayerId={currentTeamSnapshot?.wkPlayerId ?? undefined}
                />
              )}
              {activeTeamsTab === "matches" && <TeamMatches />}
              {activeTeamsTab === "stats" && <TeamInfoStats />}
              {activeTeamsTab === "photos" && (
                <div className="tournament-details tab-content team-info-photos-panel">
                  <TeamInfoPhotos />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SearchPlayerModal
        open={addPlayersModalOpen}
        onOpenChange={setAddPlayersModalOpen}
        onAddPlayers={handleAddPlayersFromSearch}
      />

      <UpdateTeamModal
        open={updateTeamModal.open}
        onOpenChange={(open) =>
          setUpdateTeamModal((prev) => ({ ...prev, open }))
        }
        teamId={updateTeamModal.teamId}
        teamSnapshotIdFromList={updateTeamModal.teamSnapshotId}
        initialData={{ teamName: updateTeamModal.teamName }}
        onSuccess={() => {
          const tid = updateTeamModal.teamId;
          if (tid) {
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.teamsSnapShotByTeamId, tid],
            });
          }
          const snap = teamSnapshotId?.trim();
          if (snap) {
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.getAllPlayers, snap],
            });
          }
          if (tournamentId) {
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.getAllTeam, tournamentId],
            });
          }
        }}
      />
    </div>
  );
};

export default TeamInfo;
